import { LitElement, css, html, nothing, unsafeHTML } from '@lit'
import * as paths from '@nova/paths'
import styles from '@nova/styles'
import { formatPrice, updateCartQuantity } from '@nova/utils'

class NovaCartItem extends LitElement {
  static properties = {
    item: {
      type: Object,
      state: true,
    },
    item_html: {
      type: String,
    },
    compareAtPrice: {
      type: Number,
    },
    inventoryPolicy: {
      type: Boolean,
    },
    max: {
      type: String,
    },
  }

  constructor() {
    super()
    this.item_html = ''
    this.compareAtPrice = 0
    this.inventoryPolicy = false
    this.max = ''
  }

  get formattedFinalPrice() {
    if (!this.item.final_price) return nothing
    return formatPrice(this.item.final_price * this.item.quantity)
  }

  get formattedCompareAtPrice() {
    if (this.compareAtPrice === 0) return nothing
    return formatPrice(this.compareAtPrice * this.item.quantity)
  }

  get formattedOriginalPrice() {
    if (!this.item.original_price) return nothing
    return formatPrice(this.item.original_price * this.item.quantity)
  }

  get formattedSavings() {
    if (this.compareAtPrice === 0) return nothing

    return `-${Math.floor(((this.compareAtPrice - this.item.original_price) * 100) / this.compareAtPrice)}%`
  }

  _renderOptions() {
    return this.item.options_with_values.map((option) => {
      if (option.name === 'Title' && option.value === 'Default Title') {
        return nothing
      }

      return html`
        <div class="cart-item__options flex">
          <dt>${option.name}:</dt>
          <dd>${option.value}</dd>
        </div>
      `
    })
  }

  _renderProperties() {
    const properties = this.item.properties
    if (!properties || typeof properties !== 'object') return nothing

    return Object.entries(properties)
      .filter(([key, value]) => {
        if (!value || String(value).trim() === '') return false
        return key.charAt(0) !== '_'
      })
      .map(
        ([key, value]) => html`
          <div class="cart-item__options flex">
            <dt>${key}:</dt>
            <dd>${value}</dd>
          </div>
        `,
      )
  }

  _handleQuantityChange = (event) => {
    this.updateQuantity(event.detail.value)
  }

  async updateQuantity(quantity) {
    this.loading = true
    await updateCartQuantity(
      [
        {
          id: this.item.key,
          quantity,
        },
      ],
      false,
    )
    this.loading = false
    document.dispatchEvent(new CustomEvent('nova:changeQuantityFinished'), {
      bubbles: true,
    })
  }

  handleRemoveLineItem() {
    const lineItemsContainer = this.parentNode
    const lineItemsArray = Array.from(lineItemsContainer.children)
    const index = lineItemsArray.indexOf(this)
    localStorage.setItem('removeLineItemIndex', index)
  }

  renderItemImage() {
    if (this.item.image_html) {
      const decodedHtml = decodeURIComponent(this.item.image_html).replace(
        /\+/g,
        ' ',
      )
      return html`${unsafeHTML(decodedHtml)}`
    }

    if (this.item.image) {
      return html`
        <img
          class="cart-item__image cover"
          src="${this.item.image}&width=200"
          alt=${this.item.product_title}
          width="100"
        />`
    }

    return html`${paths.imagePlaceholder}`
  }

  render() {
    return html`
      <div class="cart-item__media">
        <a href=${this.item.url}>
          ${this.renderItemImage()}
        </a>
      </div>
      <span class="cart-item__details">
        <a class="js-cart-item-title" href=${this.item.url}>${this.item.product_title}</a>
        ${this.item.options_with_values ? this._renderOptions() : nothing}
        ${this._renderProperties()}
      </span>
      <span class="cart-item__prices">
        <div class="cart-item__price-wrapper">
          ${Number(this.compareAtPrice) !== 0 &&
        Number(this.compareAtPrice) > Number(this.item.original_price)
        ? html`
                <dl class="cart-item__discounted-prices">
                  <dt class="visually-hidden">Save</dt>
                  <dd><span class="cart-item__saving">${this.formattedSavings}</span></dd>
                  <dt class="visually-hidden">Sale Price</dt>
                  <dd>${this.formattedFinalPrice}</dd>
                  <dt class="visually-hidden">Regular Price</dt>
                  <dd>
                    <s class="cart-item__compare-at-price">
                      ${this.formattedCompareAtPrice}
                    </s>
                  </dd>
                </dl>
              `
        : Number(this.item.original_price) !==
          Number(this.item.final_price)
          ? html`
                <dl class="cart-item__discounted-prices">
                  <dt class="visually-hidden">Sale Price</dt>
                  <dd>${this.formattedFinalPrice}</dd>
                  <dt class="visually-hidden">Regular Price</dt>
                  <dd>
                    <s class="cart-item__compare-at-price">
                      ${this.formattedOriginalPrice}
                    </s>
                  </dd>
                </dl>
              `
          : html`<span>${this.formattedFinalPrice} </span>`
      }
        </div>
      </span>
      <div class="cart-item__operations flex flex-align-center">
        <nova-quantity-input
          initial=${this.item.quantity}
          @changeQuantityStarted=${this._handleQuantityChange}
          class="cart-item__quantity-input"
          max="${!this.inventoryPolicy ? this.max : ''}"
          type="cart"
        ></nova-quantity-input>
        <sl-button
          variant="text"
          class="cart-item__remove"
          @click=${() => {
        this.updateQuantity(0)
        this.handleRemoveLineItem()
      }}
        >
          Remove
        </sl-button>
      </div>
    `
  }

  static styles = [
    styles.flex,
    styles.variables,
    css`
      :host {
        width: 100%;
        padding: 0 0 var(--nova-space-sm);
        margin: 0 0 var(--nova-space-sm);
        display: grid;
        grid-template-columns: var(--nova-logo-size-2xl) auto auto;
        grid-template-rows: auto auto;
        grid-template-areas:
          'media details price'
          'media operations operations';
        gap: var(--nova-space-sm);
      }
      :host(:not(:last-child)) {
        border-bottom: 1px solid var(--nova-brand-neutral);
      }
      .cart-item__media {
        grid-area: media;
        display: flex;
      }
      .cart-item__media a {
        height: fit-content;
        display: flex;
      }
      .cart-item__media a:focus-visible {
        border: 2px solid var(--nova-utility-focus);
      }
      .cart-item__details a:focus,
      .cart-item__media a:focus {
        border: none;
        outline: none;
        box-shadow: none;
      }
      .cart-item__media img,
      .cart-item__media svg {
        width: var(--nova-logo-size-2xl);
        height: var(--nova-logo-size-2xl);
        object-fit: cover;
        object-position: center;
      }
      .cart-item__details {
        grid-area: details;
        font: var(--nova-body-caption);
        line-height: 130%;
      }
      .cart-item__details a {
        color: var(--nova-color-text);
        text-decoration: none;
        text-transform: capitalize;
      }
      .cart-item__details a:focus-visible {
        border: 2px solid var(--nova-utility-focus);
      }
      .cart-item__options {
        text-transform: capitalize;
        margin: 10px 0;
      }
      .cart-item__options dt {
        width: 40%;
      }
      .cart-item__options dd {
        margin-left: 5px;
        overflow-wrap: break-word;
        word-break: break-word;
        width: 60%;
      }
        
      .cart-item__prices {
        grid-area: price;
        text-align: end;
        font-size: 14px;
      }
      .cart-item__prices dd {
        margin-left: 0;
      }
      .cart-item__price-wrapper .visually-hidden {
        visibility: hidden;
        display: none;
      }
      .cart-item__discounted-prices {
        margin: 0;
      }
      .cart-item__compare-at-price {
        color: var(--nova-color-foreground-secondary);
      }

      .cart-item__saving {
        color: var(--nova-brand-primary, #EB0A1E);
        font-size: 12px;
      }

      .cart-item__remove::part(base) {
        color: var(--nova-color-text);
        text-decoration: underline;
        text-underline-offset: var(--nova-space-2xs);
        font-size: 14px;
        font-style: normal;
        letter-spacing: 1.4px;
        width: min-content;
        min-height: unset;
        height: 100%;
      }
      .cart-item__remove::part(label) {
        padding: 0;
        font-weight: 700;
        display: flex;
        align-items: center;

      }
      .cart-item__operations {
        grid-area: operations;
        gap: var(--nova-space-sm);
      }
      @media screen and (min-width: 768px) {
        .cart-item__operations {
          flex-direction: row-reverse;
        }
      }
    `,
  ]
}

customElements.define('nova-cart-item', NovaCartItem)
